import { type DocNode } from '@atlaskit/adf-schema/doc';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import type { A2uiSurfaceAdapterRovoMessage } from '@atlassian/a2ui-renderer-next/v0_9/a2ui-surface-adapter-rovo';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import {
	isConversationChannelDataMessage,
	isSSEMessage,
	isStreamAgenticUi,
	isStreamAnswerPart,
	isStreamAUPError,
	isStreamError,
	isStreamResponse,
	type StreamAgenticUi,
	type StreamResponse,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import {
	CHAT_STORE_STATUS,
	type Conversation,
	isStoreAssistant,
	StoreActionLoggableName,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';

import { addAnswerPart } from './addAnswerPart';
import { addAnswerPartWithMarkdownDataMap } from './addAnswerPartWithMarkdownDataMap';
import { addAssistantMessage } from './addAssistantMessage';
import { clearCompletedTurnFlags } from './clearCompletedTurnFlags';
import { clearStatus } from './clearStatus';
import { createSafeAction, type SafeAction } from './createSafeAction';
import { createStreamMarkdownMultiTagProcessor } from './createStreamMarkdownMultiTagProcessor';
import { NoFinalResponseError } from './error-classes/NoFinalResponseError';
import { StreamErrorException } from './error-classes/StreamErrorException';
import { extractStreamMeta } from './extractStreamMeta';
import { findAddressableSurfaces } from './findAddressableSurfaces';
import { generateTmpMessageId } from './generateTmpMessageId';
import { getStoreMessageId } from './getStoreMessageId';
import { isCreateSurface } from './isCreateSurface';
import { markStreamingMessagesAsComplete } from './markStreamingMessagesAsComplete';
import { processConversationTitleUpdated } from './processConversationTitleUpdated';
import { processStreamResponseMarkdown } from './processStreamResponseMarkdown';
import { replaceAnswerData } from './replaceAnswerData';
import { routeA2UIMessages } from './routeA2UIMessages';
import { setConversationData } from './setConversationData';
import { shouldPreprocessDetailsForConversation } from './shouldPreprocessDetailsForConversation';
import { createStoreAbortController } from './store-abort-controller';
import type { StreamMeta } from './StreamMeta';
import { surfaceIdOf } from './surfaceIdOf';
import type { ChatStoreAction, MessageSource } from './types';

export type A2UIActionPayload = {
	eventName: string;
	context?: Record<string, unknown>;
	dataModel?: Record<string, unknown>;
};

export type SendA2UIActionArgs = {
	action: A2UIActionPayload;
	/** The assistant message hosting the surface the action was raised from. */
	hostMessageId: string;
	conversationId?: string;
	agentId?: string;
	webSearchEnabled?: boolean;
	conciergeEnabled?: boolean;
	useCurrentBrowsingContext?: boolean;
	appFilters?: string[];
	messageSources?: Partial<MessageSource>;
};

const A2UI_ACTION_MIME_TYPE = 'text/json';

const toStructuredActionContent = (action: A2UIActionPayload, hostMessageId: string) => ({
	data_parts_mime_type: 'application/json+a2ui',
	structured: true,
	data_parts: [
		{
			userAction: {
				actionName: action.eventName,
				context: {
					...(action.context ?? action.dataModel ?? {}),
					assistantMessageId: hostMessageId,
				},
			},
		},
	],
});

/**
 * Sends a user action raised from an A2UI surface — a button pressed, a form
 * submitted — and applies the agent's response to the chat.
 *
 * The response is split by what it carries. AGENTIC_UI ops are routed by surfaceId: updates
 * to the acted-on surface reach `hostMessageId`, while a createSurface for an unseen surface
 * opens on this turn's own message. Reply text (ANSWER_PART, or FINAL_RESPONSE content when
 * nothing streamed) is written to that same new message.
 *
 * That new message is inserted lazily, on the first reply text or new surface to arrive:
 * most surface actions answer with updates to the existing surface alone, and inserting up
 * front would leave an empty bubble behind. Under `rovo_chat_a2ui_demo_polish` it is
 * inserted up front instead, so the thinking indicator stays below the acted-on surface for the
 * whole stream, and removed when the stream ends if nothing landed in it.
 */
export const sendA2UIAction: SafeAction<[SendA2UIActionArgs]> = createSafeAction(
	StoreActionLoggableName.sendA2UIAction,
	({
		action,
		hostMessageId,
		conversationId: conversationIdParam,
		agentId,
		webSearchEnabled,
		conciergeEnabled,
		useCurrentBrowsingContext,
		appFilters,
		messageSources,
	}: SendA2UIActionArgs): ChatStoreAction =>
		async (
			{ getState, dispatch },
			{
				assistanceService,
				experienceTracker,
				getMessages,
				setMessages,
				trackAIMAUEvent,
				sendAnalyticsEvent,
			},
		) => {
			const conversationId = conversationIdParam ?? getState().currentConversationId;
			const mentionAgentsEnabled = isExperimentEnabled('rovo_chat_mention_agents');
			const insertsPendingTurnMessage = isExperimentEnabled('rovo_chat_a2ui_demo_polish');
			const tmpAssistantMessageId = generateTmpMessageId(conversationId);
			let activeAssistantMessageId = tmpAssistantMessageId;
			let hasInsertedTurnAssistantMessage = false;
			let hasTurnContent = false;
			const { abortController, storeAbortController, confirmAbort } = createStoreAbortController(
				() => (hasInsertedTurnAssistantMessage ? activeAssistantMessageId : undefined),
			);
			let turnSurfaces: Map<string, string> | undefined;
			let agentMessageId: string | undefined;
			let requestId: string | undefined;
			let humanMessageId: string | undefined;
			let streamMeta: StreamMeta | undefined;
			let initiatedTimestamp: string | undefined;
			let hasLoggedInitiated = false;
			let hasReceivedFinalResponse = false;

			const updateCurrentConversation = (data: Partial<Conversation>) => {
				dispatch(setConversationData({ conversationId, data }));
			};

			const instrumentationId = () => agentMessageId ?? tmpAssistantMessageId;

			const trackInitiated = () => {
				if (hasLoggedInitiated) {
					return;
				}
				hasLoggedInitiated = true;
				trackAIMAUEvent({
					type: 'initiated',
					subjectId: messageSources?.interactionSource,
					proactive: false,
					userGenerated: true,
					timestamp: initiatedTimestamp,
					singleInstrumentationID: instrumentationId(),
					webSearchEnabled,
					attributes: { sendMessageReason: 'A2UI_ACTION', actionName: action.eventName },
				});
			};

			const trackViewed = () => {
				trackAIMAUEvent({
					type: 'viewed',
					proactive: false,
					userGenerated: true,
					source: messageSources?.interactionSource,
					subjectId: messageSources?.followUpSource,
					messageId: agentMessageId,
					singleInstrumentationID: instrumentationId(),
					timestamp: new Date().toISOString(),
					webSearchEnabled,
					citedProductList: streamMeta?.citedProductList ?? {},
					sourceProducts: streamMeta?.sourceProducts ?? {},
					connectorInfo: streamMeta?.connectorInfo,
					responseSources: streamMeta?.responseSources,
					usesJsm: streamMeta?.usesJsm,
					messageActions: streamMeta?.messageActions,
					messageDynamicUIData: streamMeta?.messageDynamicUIData,
					invokedFrom: streamMeta?.invokedFrom,
					pluginsInvoked: streamMeta?.pluginsInvoked,
					skillsInvoked: streamMeta?.skillsInvoked,
					executedStratusSkill: streamMeta?.executedStratusSkill,
					attributes: {
						sendMessageReason: 'A2UI_ACTION',
						actionName: action.eventName,
						/** Whether the response updated the surface only, without opening a message. */
						surfaceOnlyResponse: !hasInsertedTurnAssistantMessage,
						...(streamMeta?.interactionModeExecuted
							? { interactionModeExecuted: streamMeta.interactionModeExecuted }
							: {}),
					},
				});
			};

			const trackTerminal = (type: 'error' | 'dismissed') => {
				trackInitiated();
				trackAIMAUEvent({
					type,
					userGenerated: true,
					messageId: requestId,
					subjectId: messageSources?.interactionSource,
					singleInstrumentationID: instrumentationId(),
					timestamp: new Date().toISOString(),
					webSearchEnabled,
					attributes: { sendMessageReason: 'A2UI_ACTION', actionName: action.eventName },
				});
			};

			/** Inserts this turn's assistant message, once. */
			const insertTurnAssistantMessage = () => {
				if (hasInsertedTurnAssistantMessage) {
					return;
				}
				hasInsertedTurnAssistantMessage = true;
				dispatch(
					addAssistantMessage({
						id: tmpAssistantMessageId,
						content: '',
						conversationId,
						...(mentionAgentsEnabled ? { agentAuthorId: agentId } : { agentNamedId: agentId }),
					}),
				);
			};

			/**
			 * Marks this turn's assistant message as holding reply text or a new surface, inserting
			 * it if it is not there yet. A kept message is never removed.
			 */
			const keepTurnAssistantMessage = () => {
				hasTurnContent = true;
				insertTurnAssistantMessage();
			};

			/** Removes this turn's assistant message unless it was kept. */
			const removeEmptyTurnAssistantMessage = () => {
				if (!insertsPendingTurnMessage || !hasInsertedTurnAssistantMessage || hasTurnContent) {
					return;
				}
				hasInsertedTurnAssistantMessage = false;
				setMessages(
					conversationId,
					getMessages(conversationId).filter(
						(message) =>
							!isStoreAssistant(message) || getStoreMessageId(message) !== tmpAssistantMessageId,
					),
				);
			};

			// Rewrites `<details>`/`<summary>` into the Rovo-native expand boundary — third-party
			// agents lean on it heavily. Built on the first chunk rather than here so the
			// conversation's agent metadata is settled by the time it decides.
			let markdownMultiTagProcessor: ReturnType<
				typeof createStreamMarkdownMultiTagProcessor
			> | null = null;
			const getMarkdownMultiTagProcessor = () => {
				if (!markdownMultiTagProcessor) {
					markdownMultiTagProcessor = createStreamMarkdownMultiTagProcessor({
						shouldPreprocessDetails: shouldPreprocessDetailsForConversation(
							getState(),
							conversationId,
						),
					});
				}
				return markdownMultiTagProcessor;
			};

			try {
				experienceTracker.start({
					name: ExperienceName.SEND_A2UI_ACTION,
					id: conversationId,
					attributes: { conversationId, interactionSource: messageSources?.interactionSource },
				});

				updateCurrentConversation({
					storeAbortController,
					status: CHAT_STORE_STATUS.awaitingResponse,
					lastAgentMessageStatus: 'IN_PROGRESS',
				});

				// The host's completed-turn flags would otherwise keep the thinking indicator hidden.
				setMessages(
					conversationId,
					clearCompletedTurnFlags(getMessages(conversationId), hostMessageId),
				);

				if (insertsPendingTurnMessage) {
					insertTurnAssistantMessage();
				}

				initiatedTimestamp = new Date().toISOString();
				const { generator, responseData } = await assistanceService.sendMessageStream({
					conversationId,
					// eslint-disable-next-line @atlaskit/platform/no-double-type-assertions -- Existing A2UI baseline: structured action content is adapted to the send pipeline's prompt type.
					message: toStructuredActionContent(action, hostMessageId) as unknown as DocNode,
					mimeType: A2UI_ACTION_MIME_TYPE,
					agentId,
					controller: abortController,
					storeMessage: false,
					citationsEnabled: true,
					webSearchEnabled,
					conciergeEnabled,
					useCurrentBrowsingContext,
					appFilters,
					optInSSEStream: true,
				});

				for await (const streamObj of generator) {
					const streamMessage = isSSEMessage(streamObj) ? streamObj.data : streamObj;

					if (isStreamAgenticUi(streamObj)) {
						const agenticUiMessage: StreamAgenticUi = {
							...streamObj.data,
							type: 'AGENTIC_UI',
						};
						const incomingA2UIMessages = agenticUiMessage.message.content;
						if (Array.isArray(incomingA2UIMessages)) {
							const a2uiMessages = incomingA2UIMessages as A2uiSurfaceAdapterRovoMessage[];
							turnSurfaces ??= findAddressableSurfaces(getMessages(conversationId));
							const hasNewSurface = a2uiMessages.some(
								(m) => isCreateSurface(m) && !turnSurfaces?.has(surfaceIdOf(m) ?? ''),
							);
							if (hasNewSurface) {
								keepTurnAssistantMessage();
							}
							setMessages(
								conversationId,
								routeA2UIMessages({
									messages: getMessages(conversationId),
									incomingA2UIMessages: a2uiMessages,
									createFallbackMessageId: tmpAssistantMessageId,
									addressable: turnSurfaces,
								}),
							);
						}
						continue;
					}

					if (
						processConversationTitleUpdated(
							streamMessage,
							conversationId,
							updateCurrentConversation,
						)
					) {
						continue;
					}

					if (isConversationChannelDataMessage(streamMessage)) {
						agentMessageId = streamMessage.assistant_message_id;
						trackInitiated();
					}

					// convo-ai streams an empty answer part with every A2A artifact, including an A2UI-only one.
					if (
						isStreamAnswerPart(streamMessage) &&
						(streamMessage.message.content ||
							!(
								isExperimentEnabled('jira_ai_a2ui_skip_empty_answer_part') ||
								insertsPendingTurnMessage
							))
					) {
						keepTurnAssistantMessage();
						const { processedStreamMessage, markdownData } =
							getMarkdownMultiTagProcessor()(streamMessage);
						const hasMarkdownData = Object.keys(markdownData).length > 0;
						setMessages(
							conversationId,
							hasMarkdownData
								? addAnswerPartWithMarkdownDataMap({
										chat: getMessages(conversationId),
										answerId: tmpAssistantMessageId,
										answerPart: processedStreamMessage,
										markdownData,
									})
								: addAnswerPart({
										chat: getMessages(conversationId),
										answerId: tmpAssistantMessageId,
										answerPart: processedStreamMessage,
									}),
						);
					}

					if (isStreamResponse(streamMessage)) {
						hasReceivedFinalResponse = true;
						requestId = streamMessage.metadata?.request_id ?? requestId;
						streamMeta = extractStreamMeta(
							streamMessage as StreamResponse,
							undefined,
							expValEquals('cc-maui-experiment', 'isEnabled', true),
						);
						humanMessageId = streamMeta.humanMessageId;
						const finalResponseMessageId = streamMessage.message?.message?.id?.toString();
						// Once `initiated` has been paired against an id, `viewed` has to carry the
						// same one, so the final response only supplies it when nothing has fired yet.
						if (!hasLoggedInitiated) {
							agentMessageId = finalResponseMessageId ?? agentMessageId;
						}

						const { processedStreamMessage, markdownData } = processStreamResponseMarkdown({
							streamMessage,
							messageId: finalResponseMessageId,
							conversationId,
							sendAnalyticsEvent,
							shouldPreprocessDetails: shouldPreprocessDetailsForConversation(
								getState(),
								conversationId,
							),
						});

						/**
						 * A reply whose text only arrives here still needs a message; one that
						 * carried nothing but surface ops must not get one.
						 */
						if (processedStreamMessage.message?.message?.content) {
							keepTurnAssistantMessage();
						}

						if (insertsPendingTurnMessage ? hasTurnContent : hasInsertedTurnAssistantMessage) {
							setMessages(
								conversationId,
								replaceAnswerData({
									chat: getMessages(conversationId),
									answerId: tmpAssistantMessageId,
									content: processedStreamMessage,
									humanMessageId,
									markdownData: Object.keys(markdownData).length > 0 ? markdownData : undefined,
									traceId: responseData?.traceId,
									onMessageIdChanged: (_oldId, newId) => {
										activeAssistantMessageId = newId;
									},
								}),
							);
						}
					}

					if (isStreamError(streamMessage)) {
						const { message, metadata, millisOffset, type } = streamMessage;

						// AUP violations are a valid outcome, so the experience still succeeds.
						if (isStreamAUPError(streamMessage)) {
							experienceTracker.succeed({ name: ExperienceName.SEND_A2UI_ACTION });
						}

						throw new StreamErrorException(message?.content || 'Unknown', {
							detail: { message, metadata, millisOffset, type },
							traceId: responseData?.traceId,
						});
					}
				}
			} catch (error) {
				if (error instanceof Error && error.name === 'AbortError') {
					experienceTracker.abort({ name: ExperienceName.SEND_A2UI_ACTION, reason: 'Aborted' });
					confirmAbort();
					trackTerminal('dismissed');
					return;
				}

				experienceTracker.fail({
					name: ExperienceName.SEND_A2UI_ACTION,
					error: error instanceof Error ? error : new Error(String(error)),
				});
				trackTerminal('error');
				throw error;
			} finally {
				removeEmptyTurnAssistantMessage();
				updateCurrentConversation(clearStatus({}));
				setMessages(conversationId, markStreamingMessagesAsComplete(getMessages(conversationId)));
			}

			// A programmatic abort ends the generator cleanly rather than throwing.
			if (abortController.signal.aborted) {
				experienceTracker.abort({ name: ExperienceName.SEND_A2UI_ACTION, reason: 'Aborted' });
				confirmAbort();
				trackTerminal('dismissed');
				return;
			}

			if (!hasReceivedFinalResponse) {
				const error = new NoFinalResponseError(
					'The response took too long and timed out. Try refreshing the page.',
					{ traceId: undefined },
				);
				experienceTracker.fail({ name: ExperienceName.SEND_A2UI_ACTION, error });
				trackTerminal('error');
				throw error;
			}

			trackInitiated();
			trackViewed();
			experienceTracker.succeed({ name: ExperienceName.SEND_A2UI_ACTION });
		},
);
